import { LEAGUE_COVERAGE_START, isTenureTruncated } from "./dataCoverage";

const DAY_MS = 86_400_000;
/** Gaps at or under this many days (off-season breaks) need no explanation. */
const GAP_THRESHOLD_DAYS = 45;

export interface TenureClarifier {
  /** Small muted line under the tenure row, e.g. "Stats tracked from March 2026 (earlier matches were in divisions we don't cover)." */
  headerLine: string;
  /** Long month label for the quotable summary, e.g. "March 2026". */
  trackedFromLabel: string;
}

const fmt = (d: Date, month: "short" | "long") =>
  d.toLocaleDateString("en-GB", { month, year: "numeric" });

/** First date we hold match data for a league, parsed from "2005/06" / "2005" style labels. */
function coverageStartDate(league: string): Date | null {
  const label = LEAGUE_COVERAGE_START[league];
  if (!label) return null;
  const [first, second] = label.split("/");
  const year = Number(first) < 100 ? 2000 + Number(first) : Number(first);
  const isSplitYear = second !== undefined;
  return new Date(year, isSplitYear ? 6 : 0, isSplitYear ? 1 : 1);
}

/**
 * Explains a gap between a manager's appointment and the first tracked match
 * (club was outside our tracked competitions, or our league coverage began later).
 * Returns null when nothing is misleading: no matches yet, a normal off-season
 * gap, or a tenure that predates coverage and uses the existing asterisk note.
 */
export function buildTenureClarifier(opts: {
  clubName: string;
  league: string;
  startDate: string;
  firstMatchDate: string | null | undefined;
}): TenureClarifier | null {
  const { league, startDate, firstMatchDate } = opts;
  if (!firstMatchDate) return null;
  // Pre-coverage tenures already carry the asterisk/tooltip treatment.
  if (isTenureTruncated(startDate)) return null;

  const start = new Date(startDate);
  const first = new Date(firstMatchDate);
  const gapDays = Math.round((first.getTime() - start.getTime()) / DAY_MS);
  if (gapDays <= GAP_THRESHOLD_DAYS) return null;

  const covStart = coverageStartDate(league);
  const isCoverageStart =
    !!covStart &&
    first.getTime() >= covStart.getTime() &&
    first.getTime() - covStart.getTime() <= 365 * DAY_MS;

  const headerLine = isCoverageStart
    ? `Stats tracked from ${fmt(first, "short")} (our match data for this competition starts then).`
    : `Stats tracked from ${fmt(first, "short")} (earlier matches were in divisions we don't cover).`;

  return { headerLine, trackedFromLabel: fmt(first, "long") };
}
